import React, { useState, useMemo, useEffect } from "react";
import debounce from "lodash/debounce";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@core/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@core/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@core/components/ui/command";
import { ChevronsUpDown, X } from "lucide-react";
import { useApi } from "@core/api";

interface PagedResultsTypeaheadProps {
  onReload: (filters: Record<string, any>) => Promise<{
    Results: any[];
    TotalCount: number;
    TotalPages: number;
  }>;
  onSelect?: (item: any) => void;
  paging?: {
    PageIndex: number;
    PageSize: number;
  };
  displayProperty?: string;
  displayFn?: (item: any) => string;
  defaultValue?: any;
  label?: string;
  reloadTrigger?: string;
  /** When provided and there is a selection, shows a clear button to remove the selection. */
  onClear?: () => void;
}

export const PagedResultsTypeahead = (props: PagedResultsTypeaheadProps) => {
  const [inputValue, setInputValue] = useState(props.defaultValue ? props.displayProperty
    ? props.defaultValue[props.displayProperty] || props.defaultValue.Name || props.defaultValue.Key
    : props.defaultValue.Name || props.defaultValue.Key : "");
  const [query, setQuery] = useState("");
  const [paging, setPaging] = useState(
    props.paging ?? {
      PageIndex: 0,
      PageSize: 10,
    }
  );
  const [items, setItems] = useState<any[]>([]);

  const debouncedSetQuery = useMemo(
    () =>
      debounce((val: string) => {
        setQuery(val);
        setPaging((p) => ({ ...p, PageIndex: 0 }));
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);
  
  const {
    data: result = { Results: [], TotalCount: 0, TotalPages: 0 },
    isFetching,
    refetch
  } = useQuery({
    queryKey: [props.onReload.toString(), query, paging],
    queryFn: async () => {
      return props.onReload({
        Query: query,
        ...paging,
      });
    },
  });
  
  useEffect(() => {
    if (props.reloadTrigger) {
      refetch();
    }
  }, [props.reloadTrigger, refetch])

  useEffect(() => {
    if (paging.PageIndex === 0) {
      setItems(result.Results);
    } else {
      setItems((prev) => [...prev, ...result.Results]);
    }
  }, [result.Results, paging.PageIndex]);

  const [open, setOpen] = useState(false);

  return (
    <div>
      {props.label && (
        <label className={"inline-block mb-1 text-sm dark:text-white"}>
          {props.label}
        </label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            type="button"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span>{inputValue || "Select..."}</span>
            <div className="flex items-center gap-1 shrink-0">
              {inputValue && props.onClear && (
                <button
                  type="button"
                  className="rounded p-0.5 hover:bg-black/10 dark:hover:bg-white/10 opacity-70"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setInputValue("");
                    props.onClear?.();
                  }}
                  aria-label="Clear selection"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search..."
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                setInputValue(e.target.value);
                debouncedSetQuery(e.target.value);
              }}
            />
            <CommandList className="overflow-y-auto">
              {isFetching ? (
                <div className="text-sm text-center p-2">Loading...</div>
              ) : (
                <>
                  <CommandEmpty>No results found.</CommandEmpty>
                  <CommandGroup>
                    {items.sort((a, b) => {
                      if (a.Id < b.Id) return -1;
                      if (a.Id > b.Id) return 1;
                      return 0;
                    }).map((item, index) => {
                      return (
                        <CommandItem
                          key={index}
                          value={item.Id}
                          onSelect={() => {
                            setInputValue(
                              props.displayProperty
                                ? item[props.displayProperty] || item.Name || item.Key
                                : item.Name || item.Key
                            );
                            setOpen(false);
                            if (props.onSelect) {
                              props.onSelect(item);
                            }
                          }}
                        >
                          <span>{props.displayFn ? props.displayFn(item) : `${item.Key} - ${props.displayProperty
                            ? item[props.displayProperty] || item.Name || item.Key
                            : item.Name || item.Key}`}</span>
                        </CommandItem>
                      );
                    })}
                    {paging.PageIndex + 1 < result.TotalPages && (
                      <CommandItem
                        onSelect={() =>
                          setPaging((p) => ({ ...p, PageIndex: p.PageIndex + 1 }))
                        }
                      >
                        Load more...
                      </CommandItem>
                    )}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
